<template>
  <div
    class="row hosting-content-row"
    v-loading="collectionsViewerIsLoading"
    :element-loading-text="importArticleLoader"
    :key="`${focusContentId}-${editingLanguage}`"
    ref="parametricContentBuilder"
  >
    <div class="container">
      <div
        class="position-relative"
        :class="displayOnWebParametric"
        v-if="rootId"
      >
        <div
          v-if="!languageIsSet && focusKnowledgeIsMultilinguale"
          class="row justify-content-center untranslate-content"
        >
          <div class="col-lg-9 col-md-10 col-sm-12">
            <div class="content-blur"></div>
          </div>
        </div>
        <KnowledgeEditorMainTitle
          class="row"
          :content="root"
          :contributors="contributors"
          :total="total"
          :step-id="stepId"
          :backlinks="focusContentBacklinks"
          :editable="editable"
          :display-modal="displayModal"
          @update-display-modal="displayModal = $event"
          @update-content="handleUpdateContent($event, 'root')"
          @open-content-settings="$emit('open-content-settings', $event)"
          @set-content-trust="setContentTrustMiddleware"
          @upsert-verification-policy="handleUpsertVerificationPolicy"
          @delete-verification-policy="handleDeleteVerificationPolicy"
          @set-similar-content-id="similarContentId = $event"
          @open-set-language-modal="openSetLanguageModal"
          @unset-language="openUnsetLanguageModal($event)"
        />
        <div class="row content-editor">
          <div class="col-12 content-col">
            <ArticleBuilder
              v-if="root.type === 'Article'"
              :editable="editable"
              :is-content-editable="isContentEditable"
              :content="root"
              v-loading="importArticleIsLoading"
              @update-written-content="delayedUpdateContent"
              @open-feedback-modal="openFeedbackModal(root)"
              @update-content="handleUpdateContent"
              @add-notification="addNotification($event)"
            />
            <DiagnosticBuilder
              v-if="root.type === 'Diagnostic'"
              :editable="editable"
              :is-content-editable="isContentEditable"
              :root="root"
              :step="step"
              :new-automation-id="newAutomationId"
              @focus-node="focusNode"
              @add-child="addChild"
              @delete-child="deleteChild(step.id, $event)"
              @update-children-order="updateChildrenOrder"
              @toggle-key-step="handleToggleKeyStep"
              @update-content="handleUpdateContent"
              @update-content-actions="handleUpdateContentActions"
              @delayed-update-content-actions="delayedUpdateContentActions"
              @update-written-content="delayedUpdateContent"
              @relocate-child="relocateChildLocal"
              @open-feedback-modal="openFeedbackModal(step)"
              @add-notification="addNotification($event)"
              @create-automation="handleCreateAutomation"
              @update-automation="handleUpdateAutomation"
              @delete-automation="handleDeleteAutomation"
            />
          </div>
        </div>
      </div>
    </div>
    <SimilarContentToast
      v-if="displaySimilarContentToast"
      class="toast-position"
      @go-to-content="goToSimilarContent"
    />
    <SetLanguageToast
      v-if="displaySetLanguageToast"
      class="toast-position"
      :language="editingLanguage"
      :entity-type="root.type"
      @open-set-language-modal="openSetLanguageModal"
    />
    <SetLanguageModal
      v-if="displaySetLanguageModal"
      :display="setLanguageModal"
      :language="language"
      @set-new-language="setNewLanguage"
      @close="
        setLanguageModal = false;
        language = '';
      "
    />
    <ModalEvents
      modal-name="DeleteContentModal"
      :uid="unsetLanguageModalUniqueKey"
      @choice="unsetLanguage"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import debounce from 'lodash.debounce';

// COMPONENTS
import ArticleBuilder from './ParametricArticleBuilder';
import DiagnosticBuilder from './ParametricDiagnosticBuilder/ParametricDiagnosticBuilder';
import KnowledgeEditorMainTitle from './ParametricContentMainTitle/KnowledgeEditorMainTitle';
import SetLanguageToast from '@/components/Modals/SetLanguageToast';
import SetLanguageModal from '@/components/Modals/SetLanguageModal';
import SimilarContentToast from '@/components/Modals/SimilarContentToast';
import ModalEvents from '@/components/Modals/ModalEvents';

import KnowledgeMixin from 'mixins/KnowledgeMixin/KnowledgeMixin';

export default {
  name: 'parametric-content-builder',
  mixins: [KnowledgeMixin],
  components: {
    KnowledgeEditorMainTitle,
    ArticleBuilder,
    DiagnosticBuilder,
    SimilarContentToast,
    SetLanguageToast,
    SetLanguageModal,
    ModalEvents,
  },
  props: {
    editable: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      isLoading: false,
      content: this.focusContent,
      contributors: [],
      total: 0,
      step: {},
      diagBuilderKey: 0,
      displayModal: false,
      newAutomationId: null,
      debounceUpdateContent: debounce(function (variables) {
        this.handleUpdateContent(variables);
      }, 2000),
      debounceUpdateContentActions: debounce(function (variables) {
        this.handleUpdateContentActions(variables);
      }, 1000),
      setLanguageModal: false,
      language: '',
      similarContentId: '',
      languageToUnset: '',
    };
  },
  computed: {
    isContentEditable() {
      return (
        this.editable &&
        !this.root.isReadOnly &&
        this.root.userPermissions &&
        Boolean(this.root.userPermissions.canUpdateContent)
      );
    },
    isParametric() {
      return this.$route.name.includes('web-parametric');
    },
    displayOnWebParametric() {
      return this.isParametric ? 'col-12' : 'container';
    },
    displaySimilarContentToast() {
      return (
        !this.isParametric &&
        !this.collectionsViewerIsLoading &&
        !!this.similarContentId
      );
    },
    importArticleLoader() {
      return this.importArticleIsLoading
        ? this.$t(
            'knowledge.article-builder.side-bar.imported-articles.loading',
          )
        : '';
    },
    rootId() {
      return this.root ? this.root.id : null;
    },
    languageIsSet() {
      return (
        // - setLanguages can be empty if migration failed. In that case we don't want display toast because kb is monolingual 'default'
        // - if editingLanguage is not in setLanguages so the content is not translate
        !this.root ||
        !this.root.setLanguages.length ||
        !!this.root.setLanguages.filter((lang) => lang === this.editingLanguage)
          .length
      );
    },
    displaySetLanguageToast() {
      return (
        this.focusKnowledgeIsMultilinguale &&
        !this.isParametric &&
        !this.collectionsViewerIsLoading &&
        !this.languageIsSet &&
        !this.language
      );
    },
    displaySetLanguageModal() {
      return this.focusKnowledgeIsMultilinguale && this.setLanguageModal;
    },
    unsetLanguageModalUniqueKey() {
      return `unset-content-${this.rootId}`;
    },
    stepId() {
      return this.step ? this.step.id : null;
    },
    root: {
      get() {
        this.forceStepValue();
        return this.focusContent;
      },
      set(val) {
        this.$emit('change-route-type', val.type);
      },
    },
    ...mapGetters('knowledgeModule', [
      'focusKnowledgeIsMultilinguale',
      'focusContent',
      'focusContentId',
      'focusContentBacklinks',
      'collectionsViewerIsLoading',
      'importArticleIsLoading',
      'editingLanguage',
    ]),
    ...mapGetters('adminModule', ['reviewers']),
  },
  methods: {
    async goToSimilarContent() {
      const path = this.$route.path.includes('web-parametric')
        ? '/web-parametric/content/'
        : '/knowledge/content/';

      this.$router.push({
        path: path + this.similarContentId,
      });
      this.similarContentId = '';
    },
    async handleCreateAutomation(automation) {
      const newAutomation = await this.createAutomation(automation);

      if (this.rootId !== this.stepId) {
        const stepAutomations = this.step.automations
          ? [...this.step.automations]
          : [];
        stepAutomations.push(newAutomation);
        this.step.automations = stepAutomations;
      }

      this.newAutomationId = newAutomation.id;
      this.$message({
        message: this.$t('automations.confirmations.create'),
        type: 'success',
      });
    },
    async handleUpdateAutomation(automation) {
      const newAutomation = await this.updateAutomation(automation);

      if (this.rootId !== this.stepId) {
        const stepAutomations = [...this.step.automations];
        const index = stepAutomations.findIndex(
          (automationItem) => automationItem.id === automation.id,
        );

        if (index === -1) return;

        stepAutomations.splice(index, 1, newAutomation);

        this.step.automations = stepAutomations;
      }

      this.$message({
        message: this.$t('automations.confirmations.update'),
      });
    },
    async handleDeleteAutomation(id) {
      await this.deleteAutomation(id);

      if (this.rootId !== this.stepId) {
        const stepAutomations = [...this.step.automations];
        this.step.automations = stepAutomations.filter(
          (automation) => automation.id !== id,
        );
      }

      this.$message({
        message: this.$t('automations.confirmations.delete'),
        type: 'error',
      });
    },
    forceStepValue() {
      if (!this.$route.params.stepId) this.step = { ...this.focusContent };
    },
    // UPDATE METHODS
    async handleUpdateContent({ id, payload, source }) {
      let newContent;
      if (payload.body && payload.body === this.root.body) return;
      // The mutation in DB is done through setContentRedirection in PopoverRedirection component
      // Here we only want to update the local state
      if (payload.redirection) {
        const { entityId, entityType } = payload.redirection;

        const redirectedStepIdx = this.step.children.findIndex(
          (child) => child.id === id,
        );

        if (redirectedStepIdx === -1) return;

        newContent = {
          ...this.step.children[redirectedStepIdx],
          redirection: entityId
            ? {
                isDeleted: false,
                entityId,
                entityType,
              }
            : undefined,
        };
      } else {
        const { body } = payload;
        const domParser = new DOMParser().parseFromString(body, 'text/html');

        const inlineMentions = domParser.querySelectorAll('.inline-mention');
        const resolveMentions = Array.from(inlineMentions).map((el) => {
          return {
            targetId: el.attributes['data-node-id'].value,
            targetType: el.attributes['data-node-type'].value,
            blockId: el.parentNode.attributes['data-uid'].value,
          };
        });

        const uniqueMentions = [...new Set(resolveMentions)];

        newContent = await this.updateContent({
          id,
          payload: { ...payload, mentions: uniqueMentions },
        });
      }

      this.localUpdateContent(newContent, source);
    },
    async handleToggleKeyStep({ id, type }) {
      await this.toggleKeyStep({ id, type });
      this.step.type = type;
    },
    async handleUpdateContentActions(newActions) {
      this.updateContentActions({
        id: this.root.type === 'Article' ? this.rootId : this.stepId,
        actions: newActions,
      });

      if (this.rootId !== this.stepId) {
        const stepCopy = { ...this.step, actions: newActions };
        this.step = stepCopy;
      }
    },
    async updateSettings({ id, payload }) {
      await this.updateContentSettingsAction({
        id,
        payload,
      });
    },
    async archiveContent({ id }) {
      const success =
        await this.$services.parametricContents.bulkArchiveContents([id]);
      if (success) this.$router.go(-1);
    },
    async deleteChild(parentId, id) {
      const success =
        await this.$services.parametricContents.bulkArchiveContents([id]);
      if (success) {
        this.localUpdateContent({
          id: parentId,
          path: this.step.path.filter((el) => el !== id),
          children: this.step.children.reduce((acc, el) => {
            if (el.redirection && el.redirection.entityId === id)
              el.redirection.isDeleted = true;
            if (el.id !== id) acc.push(el);
            return acc;
          }, []),
        });
      }
    },
    async updateChildrenOrder({ id, payload, source }) {
      const newChildren = payload.path.map((id) =>
        this.step.children.find((c) => c.id === id),
      );
      this.localUpdateContent({
        id,
        path: payload.path,
        children: newChildren,
      });
      await this.handleUpdateContent({ id, payload, source });
      this.$root.$emit('UPDATE_IS_LOADING', false);
    },
    async addChild(payload) {
      const { id, type, label } = payload;

      const lang = this.editingLanguage;
      const newChild = await this.$services.parametricContents.addChild(
        id,
        type,
        label.trim(),
        lang,
      );
      newChild;
      this.step = Object.assign({}, this.step, {
        path: this.step.path.concat([newChild.id]),
        children: this.step.children.concat([newChild]),
      });
      if (this.stepId === this.rootId) this.root = { ...this.step };
    },
    async relocateChildLocal({
      childId,
      formerParentId,
      newParentId,
      onlyLocalUpdate = false,
    }) {
      if (!onlyLocalUpdate) {
        await this.relocateChild({
          childId,
          formerParentId,
          newParentId,
        });
      }
      this.localUpdateContent({
        id: this.step.id,
        path: this.step.path.filter((id) => id !== childId),
        children: this.step.children.filter((child) => child.id !== childId),
      });
    },
    async setContentTrustMiddleware(event) {
      await this.bulkUpdateStatus({
        contentIds: [this.rootId],
        key: 'isOutdated',
        value: !event.trust,
      });
      const message = `knowledge.verification-policy.${
        event.trust ? 'trust-message' : 'untrust-message'
      }`;
      this.$message({
        message: this.$t(message),
        type: 'success',
      });
    },
    async handleUpsertVerificationPolicy(event) {
      await this.upsertVerificationPolicy(event);
      this.displayModal = false;
      return this.$message({
        message: this.$t('knowledge.verification-policy.update-message'),
        type: 'success',
      });
    },
    async handleDeleteVerificationPolicy(event) {
      await this.deleteVerificationPolicy(event);
      this.displayModal = false;
      return this.$message({
        message: this.$t('knowledge.verification-policy.delete-message'),
        type: 'success',
      });
      // });
    },
    async handleUpdateUnpublishDate(event) {
      await this.updateUnpublishDate(event);
    },
    // DEBOUNCE CONTENT UPDATE
    delayedUpdateContent({ id, payload }) {
      this.debounceUpdateContent({
        id,
        payload,
        source: 'focus',
      });
    },

    delayedUpdateContentActions(newActions) {
      this.debounceUpdateContentActions(newActions);
    },

    // UTILS
    focusNode(node) {
      this.step = Object.assign({}, this.step, node);
      if (this.step && ['Step', 'keyStep'].includes(this.step.type)) {
        this.setFocusStep(this.step);
      }
    },
    localUpdateContent(newContent, source = 'focus') {
      const { id } = newContent;
      if (source === 'root') {
        this.root = Object.assign({}, this.root, newContent);
        if (id === this.stepId) this.step = { ...this.root };
      } else if (source === 'focus') {
        if (id === this.stepId) {
          this.step = Object.assign({}, this.step, newContent);
          if (id === this.rootId) this.root = { ...this.step };
        }
      } else if (source === 'child') {
        const childIdx = this.step.children.findIndex(
          (c) => c.id === newContent.id,
        );
        if (childIdx !== -1) {
          const children = this.step.children;
          children[childIdx] = Object.assign(
            {},
            children[childIdx],
            newContent,
          );
          this.step = {
            ...this.step,
            children,
          };
        }
      }
    },

    updateDisplayModal(event) {
      this.displayModal = event;
    },
    async displayMoreOptions() {
      this.product.resolutions =
        await this.$services.products.getContentsByProductId(this.product.id);
    },
    openFeedbackModal(content) {
      this.$emit('open-feedback-modal', content);
    },
    hideFeedbackModal() {
      this.$emit('close-feedback-modal');
    },
    async addNotification(notification) {
      try {
        await this.addAgentNotification({
          notification: {
            ...notification,
            companyId: this.$store.state.companyId,
          },
        });
        this.$message({
          message: `La notification a bien été ajoutée`,
          type: 'success',
        });
      } catch (e) {
        this.$message({
          message: `Une erreur s'est produite`,
          type: 'error',
        });
      }
    },
    openSetLanguageModal(lang) {
      this.setLanguageModal = true;
      this.language = lang || this.editingLanguage;
    },
    setNewLanguage(label) {
      this.updateContent({
        id: this.root.id,
        payload: {
          label: label,
          body: this.root.body,
          lang: this.language,
        },
        source: 'root',
      });
      this.setLanguageModal = false;
      this.language = '';
    },
    openUnsetLanguageModal(lang) {
      this.languageToUnset = lang;
      this.openModal({
        component: 'DeleteContentModal',
        uid: this.unsetLanguageModalUniqueKey,
        props: { display: true, lang, deleteType: 'language' },
      });
    },
    unsetLanguage(modalResp) {
      if (!modalResp) return;
      this.unsetContentLanguage({
        contentId: this.root.id,
        lang: this.languageToUnset,
      });
    },
    storeEvent() {
      if (this.$route.path.includes('web-parametric')) {
        this.$services.events.content.read(
          this.step,
          window.location.href,
          'WEB_PARAMETRIC',
        );
      }
    },
    handleOnScroll() {
      const editorDragHandleElement = document.getElementsByClassName(
        'mayday-editor__drag-icon-container',
      );

      if (!editorDragHandleElement || !editorDragHandleElement[0]) return;

      editorDragHandleElement[0].style.visibility = 'hidden';
    },
    async parseContributors(content) {
      if (!this.reviewers || !this.reviewers.users)
        await this.getCompanyReviewers();
      const data = await this.fetchContentHistory({
        id: content.id,
        limit: 3,
        page: 1,
        lang: content.returnLanguage,
      });
      const results = data.results.sort((a, b) => b.updatedAt - a.updatedAt);
      if (
        data.total < 3 &&
        !results.find((contributor) =>
          content.enrichedAuthor
            ? contributor.authorId === content.enrichedAuthor.id
            : contributor.authorId === content.author,
        )
      ) {
        if (!content.author && !content.enrichedAuthor.id) {
          results.push({
            authorId: '0',
            username: this.$t('knowledge.contributors.deleted-user'),
            updatedAt: content.createdAt,
          });
        } else
          results.push({
            authorId: content.author || content.enrichedAuthor.id,
            username: content.enrichedAuthor
              ? content.enrichedAuthor.username
              : this.$t('knowledge.contributors.deleted-user'),
            updatedAt: content.createdAt,
          });
        data.total++;
      }
      return { total: data.total, contributors: results };
    },

    ...mapActions('kbStore', ['pushContentToCasePath']),
    ...mapActions('knowledgeModule', [
      'updateContent',
      'toggleKeyStep',
      'updateContentActions',
      'updateContentSettingsAction',
      'bulkUpdateStatus',
      'createAutomation',
      'updateAutomation',
      'deleteAutomation',
      'upsertVerificationPolicy',
      'deleteVerificationPolicy',
      'updateUnpublishDate',
      'unsetContentLanguage',
      'relocateChild',
      'fetchContentHistory',
      'setFocusStep',
    ]),
    ...mapActions('notificationModule', ['addAgentNotification']),
    ...mapActions('modalsModule', ['openModal']),
    ...mapActions('adminModule', ['getCompanyReviewers', 'getContributors']),
  },
  watch: {
    ['$route.path']: {
      handler: async function () {
        await this.getContributors();
      },
      immediate: true,
    },
    async $route(to, from) {
      if (!to.params.stepId) {
        this.step = Object.assign({}, this.root);
      }
      if (
        `${to.params.contentId}/${to.params.stepId}` !==
        `${from.params.contentId}/${from.params.stepId}`
      ) {
        this.storeEvent();
      }
      this.similarContentId = '';
    },
    async focusContent(newVal, oldVal) {
      if ((newVal && !oldVal) || newVal.id !== oldVal.id) {
        await this.pushContentToCasePath(newVal);
        const { total, contributors } = await this.parseContributors(newVal);
        this.total = total;
        this.contributors = contributors;
        if (!newVal.enrichedAuthor.id)
          newVal.enrichedAuthor.username = this.$t(
            'knowledge.contributors.deleted-user',
          );
      } else if (
        newVal &&
        oldVal &&
        parseInt(newVal.updatedAt) > parseInt(oldVal.updatedAt)
      ) {
        await this.pushContentToCasePath(newVal);
        const { total, contributors } = await this.parseContributors(newVal);
        this.total = total;
        this.contributors = contributors;
        if (!newVal.enrichedAuthor.id)
          newVal.enrichedAuthor.username = this.$t(
            'knowledge.contributors.deleted-user',
          );
      }
    },
    stepId: {
      handler: function () {
        this.debounceUpdateContent.flush();
        this.debounceUpdateContentActions.flush();
      },
      immediate: true,
    },
    root() {
      this.diagBuilderKey += 1;
    },
  },
  async mounted() {
    const container = this.$refs['parametricContentBuilder'];
    await this.getCompanyReviewers();
    if (this.root) {
      const { total, contributors } = await this.parseContributors(this.root);
      this.total = total;
      this.contributors = contributors;
      if (!this.root.enrichedAuthor.id)
        this.root.enrichedAuthor.username = this.$t(
          'knowledge.contributors.deleted-user',
        );
    }
    container.addEventListener('scroll', this.handleOnScroll);
  },
  beforeDestroy() {
    this.debounceUpdateContent.flush();
    this.debounceUpdateContentActions.flush();
    const container = this.$refs['parametricContentBuilder'];
    container.removeEventListener('scroll', this.handleOnScroll);
  },
};
</script>

<style lang="scss" scoped>
.hosting-content-row {
  height: calc(100vh - 60px);
  overflow: auto;
  position: relative;
  margin-right: 0px;
}

.untranslate-content {
  position: absolute;
  top: -40px;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 998;
}

.content-blur {
  width: 100%;
  height: 100%;
  background: rgba(246, 249, 252, 0.5);
}

.toast-position {
  top: 45px;
  right: 12px;
}

.collapse-enter-active {
  animation: collapse-enter 0.4s ease-in-out;
}

.collapse-leave-active {
  animation: collapse-enter 0.4s ease-in-out reverse;
}

@keyframes collapse-enter {
  0% {
    transform: translateY(300px);
  }
  100% {
    transform: translateY(0);
  }
}
</style>
